import 'css/src/components/common/dashboard-sidebar.module.scss';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { RoleEnum, Urls } from 'js/src/client';
import { DashboardProjectList } from 'js/src/components/common/DashboardProjectList';
import HeaderLogo from 'js/src/components/common/HeaderLogo';
import WorkspaceSelector from 'js/src/components/common/WorkspaceSelector';
import { getIsUserAllowedAccessToAccessBlueprintsAndBrandKits } from 'js/src/data/account';
import {
    ALL_VIDEOS_VIEW_ID,
    CREATE_VIEW_ID,
    DASHBOARD_ROUTES,
    navigateToDashboardPath,
    resetSearchedVideos,
    selectActiveView,
    TRASH_VIDEOS_VIEW_ID,
    userCanAccessCreateView,
    viewIdFromPath,
} from 'js/src/data/dashboard';
import { MODAL_IDS, showModal } from 'js/src/data/modals';
import { COLOR_L5_GREY_700, COLOR_L5_PRIMARY_500, roleCanDeleteVideos } from 'js/src/libs/consts';
import BlueprintsIcon from 'js/src/libs/custom_icons/BlueprintsIcon';
import Flags from 'js/src/libs/flags';
import Navigation from 'js/src/libs/navigation';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { hasFeatureEnabled } from 'js/src/libs/server-utils';

// True only when this page is the dashboard SPA itself (which mounts the
// dashboard reducer). Sub-paths like /dashboard/analytics/ are separate
// entry points whose store does NOT include the dashboard slice — calling
// `selectActiveView` there would crash. We match any /dashboard/<tab>/
// URL that the SPA itself owns (create / videos / trash / projects/<id>),
// plus the bare /dashboard/ entry point (which client-side redirects to a tab).
const IS_ON_DASHBOARD =
    typeof SERVER !== 'undefined' &&
    (window.location.pathname === Urls.DASHBOARD ||
        viewIdFromPath(window.location.pathname) !== null);

/**
 * The DashboardSidebar is used to frame all "dashboard" pages. That includes
 * pages like the dashboard (which lists the user's videos), the instant videos page, etc.
 * Sometimes luminary is available (on the dashboard), sometimes not.
 */

type DashboardSidebarPropsFromRedux = ConnectedProps<typeof connector>;

const DashboardSidebarPresentation = React.memo(function DashboardSidebarPresentation({
    activeView,
    userCanAccessBlueprintsAndBrandKits,
    isStaff,
    showModal,
    resetSearchedVideos,
}: DashboardSidebarPropsFromRedux) {
    const handleProjectCreation = React.useCallback(() => {
        showModal({ modal_id: MODAL_IDS.PROJECT_CREATE });
    }, [showModal]);

    return (
        <div className="dashboard-sidebar">
            <div className="overflow-container overflow-y-auto">
                <div className="m-l5-16 ms-l5-24 pt-l5-2">
                    <HeaderLogo />
                </div>
                <div className="mx-l5-8">
                    <WorkspaceSelector />
                </div>
                <ul className="navigation navigation-main navigation-accordion">
                    <NavigationList
                        activeView={activeView}
                        resetSearchedVideos={resetSearchedVideos}
                        userCanAccessBlueprintsAndBrandKits={userCanAccessBlueprintsAndBrandKits}
                        isStaff={isStaff}
                    />
                    <br />
                    {IS_ON_DASHBOARD && (
                        <DashboardProjectList onProjectAdd={handleProjectCreation} />
                    )}
                </ul>
            </div>
        </div>
    );
});

const mapStateToProps = (state: RootState) => {
    return {
        activeView: IS_ON_DASHBOARD ? selectActiveView(state) : undefined,
        userCanAccessBlueprintsAndBrandKits:
            getIsUserAllowedAccessToAccessBlueprintsAndBrandKits(state),
        isStaff: state.account.user_profile.is_staff,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            showModal,
            resetSearchedVideos,
        },
        dispatch,
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const DashboardSidebar = connector(DashboardSidebarPresentation);

export default DashboardSidebar;

/*******************************
 * PRESENTATION HELPERS
 ******************************/

/**
 * Renders list items for navigating to video related urls
 */
type NavigationListProps = {
    activeView?: number;
    resetSearchedVideos: VoidFunction;
    userCanAccessBlueprintsAndBrandKits: boolean;
    isStaff: boolean;
};

const NavigationList: React.FC<NavigationListProps> = React.memo(function NavigationList({
    activeView,
    resetSearchedVideos,
    userCanAccessBlueprintsAndBrandKits,
    isStaff,
}) {
    const currUrl = window.location.pathname;
    const isCreateNavActive = activeView === CREATE_VIEW_ID;
    const isAllVideosNavActive = activeView === ALL_VIDEOS_VIEW_ID;
    const isTrashNavActive = activeView === TRASH_VIDEOS_VIEW_ID;
    const showCreateTab = userCanAccessCreateView();

    const showCreate = React.useCallback(() => {
        if (IS_ON_DASHBOARD) {
            navigateToDashboardPath(DASHBOARD_ROUTES.CREATE);
        } else {
            Navigation.goToLocation(DASHBOARD_ROUTES.CREATE);
        }
    }, []);

    const showAllVideos = React.useCallback(() => {
        if (IS_ON_DASHBOARD) {
            navigateToDashboardPath(DASHBOARD_ROUTES.VIDEOS);
            resetSearchedVideos();
        } else {
            Navigation.goToLocation(DASHBOARD_ROUTES.VIDEOS);
        }
    }, [resetSearchedVideos]);

    const showTrashVideos = React.useCallback(() => {
        if (!IS_ON_DASHBOARD) {
            throw new Error('showTrashVideos should only be called on the dashboard');
        }
        navigateToDashboardPath(DASHBOARD_ROUTES.TRASH);
        resetSearchedVideos();
    }, [resetSearchedVideos]);

    const canAccessTrash = IS_ON_DASHBOARD && roleCanDeleteVideos(SERVER.USER.role);

    return (
        <>
            {showCreateTab && (
                <li className={isCreateNavActive ? 'active' : ''} id="dashboard-create">
                    <a onClick={showCreate}>
                        <i className="material-symbols-outlined">auto_awesome</i>
                        <span>Create</span>
                    </a>
                </li>
            )}
            <li className={isAllVideosNavActive ? 'active' : ''} id="dashboard-videos">
                <a onClick={showAllVideos}>
                    <i className="material-symbols-outlined">ondemand_video</i>
                    <span>All videos</span>
                </a>
            </li>
            {userCanAccessBlueprintsAndBrandKits && (
                <li className={currUrl === Urls.BRAND_KITS ? 'active' : ''} id="brand-kits">
                    <a href={Urls.BRAND_KITS}>
                        <i className="material-symbols-outlined">palette</i>
                        <span>Brand kits</span>
                    </a>
                </li>
            )}
            {userCanAccessBlueprintsAndBrandKits && (
                <>
                    <li
                        className={currUrl === Urls.BLUEPRINTS_PAGE ? 'active' : ''}
                        id="blueprints"
                    >
                        <a href={Urls.BLUEPRINTS_PAGE}>
                            <>
                                <i className="material-symbols-outlined">
                                    <BlueprintsIcon
                                        fillColor={
                                            currUrl === Urls.BLUEPRINTS_PAGE
                                                ? COLOR_L5_PRIMARY_500
                                                : COLOR_L5_GREY_700
                                        }
                                    />
                                </i>
                                <span>Blueprints</span>
                            </>
                        </a>
                    </li>
                </>
            )}
            {SERVER.USER.role === RoleEnum.ADMIN && (
                <li className={currUrl === Urls.ANALYTICS ? 'active' : ''}>
                    <a href={Urls.ANALYTICS}>
                        <i className="material-symbols-outlined">insert_chart</i>
                        <span>Analytics</span>
                    </a>
                </li>
            )}
            {Flags.canUseVideoCreationApi() && (
                <>
                    <li
                        className={currUrl === Urls.VIDEO_CREATION_API ? 'active' : ''}
                        id="video-creation-api"
                    >
                        <a href={Urls.VIDEO_CREATION_API}>
                            <i className="material-symbols-outlined">integration_instructions</i>
                            <span>API</span>
                        </a>
                    </li>
                    {(currUrl === Urls.VIDEO_CREATION_API ||
                        currUrl === Urls.VIDEO_CREATION_API_PRESETS) && (
                        <li
                            className={`nav-sub-item ${currUrl === Urls.VIDEO_CREATION_API_PRESETS ? 'active' : ''}`}
                            id="video-creation-api-presets"
                        >
                            <a href={Urls.VIDEO_CREATION_API_PRESETS}>
                                <i className="material-symbols-outlined">key</i>
                                <span>API Keys</span>
                            </a>
                        </li>
                    )}
                </>
            )}
            {isStaff && hasFeatureEnabled('genx_preset_editor') && (
                <>
                    <li
                        className={currUrl === '/genx-preset-builder/' ? 'active' : ''}
                        id="genx-preset-builder"
                    >
                        <a href="/genx-preset-builder/">
                            <i className="material-symbols-outlined">tune</i>
                            <span>Preset Builder</span>
                        </a>
                    </li>
                    <li
                        className={currUrl === '/preset-tester/' ? 'active' : ''}
                        id="preset-tester"
                    >
                        <a href="/preset-tester/">
                            <i className="material-symbols-outlined">lab_research</i>
                            <span>Preset Tester</span>
                        </a>
                    </li>
                </>
            )}
            {canAccessTrash && (
                <li className={isTrashNavActive ? 'active' : ''} id="trash-videos">
                    <a onClick={showTrashVideos}>
                        <i className="material-symbols-outlined">delete</i>
                        <span>Trash</span>
                    </a>
                </li>
            )}
        </>
    );
});
